/* eslint-disable react/no-array-index-key */
import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import {
  TradingOffer,
  PartnerAgreedTradingOffer,
} from '../../../types/TradingOffer';

type TradingOfferAcceptanceProgressProps = {
  offer: TradingOffer;
  acceptanceHistory: PartnerAgreedTradingOffer[];
};

const TradingOfferAcceptanceProgress: FC<
  TradingOfferAcceptanceProgressProps
> = ({ offer, acceptanceHistory }) => {
  const { t } = useTranslate();

  const totalShares = offer.sharesCount.total;
  const agreedShares = offer.sharesCount.agreed;
  const availableShares = totalShares - agreedShares;

  const progressPercent = useMemo(() => {
    if (totalShares === 0) return 0;
    return Math.round((agreedShares / totalShares) * 100);
  }, [agreedShares, totalShares]);

  return (
    <div className="card card-bordered">
      <div className="card-inner">
        <h6 className="card-title text-base">{t('AcceptanceProgress')}</h6>

        <div className="progress-wrap mb-3">
          <div className="progress-text d-flex justify-content-between mb-1">
            <span className="sub-text">
              {formatNumber(agreedShares)} / {formatNumber(totalShares)}{' '}
              {t('Shares').toLowerCase()}
            </span>
            <span className="sub-text">{progressPercent}%</span>
          </div>
          <div className="progress progress-md">
            <div
              className="progress-bar bg-primary"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-4 text-center">
            <span className="d-block sub-text">{t('Offered')}</span>
            <span className="d-block lead-text">
              {formatNumber(totalShares)}
            </span>
          </div>
          <div className="col-4 text-center">
            <span className="d-block sub-text">{t('Accepted')}</span>
            <span className="d-block lead-text text-primary">
              {formatNumber(agreedShares)}
            </span>
          </div>
          <div className="col-4 text-center">
            <span className="d-block sub-text">{t('Available')}</span>
            <span className="d-block lead-text text-success">
              {formatNumber(availableShares)}
            </span>
          </div>
        </div>

        {acceptanceHistory.length > 0 && (
          <>
            <h6 className="overline-title-alt mt-4 mb-3">
              {t('AcceptanceHistory')}
            </h6>
            <ul className="nk-activity">
              {acceptanceHistory.map((item, index) => (
                <li key={index} className="nk-activity-item">
                  <div className="nk-activity-media user-avatar bg-primary-dim">
                    <em
                      className={`icon ni ${
                        item.isApproved ? 'ni-check' : 'ni-clock'
                      }`}
                    />
                  </div>
                  <div className="nk-activity-data">
                    <div className="label">
                      {formatNumber(item.sharesCount.agreed)}{' '}
                      {t('Shares').toLowerCase()}
                    </div>
                    <span className="time">
                      {format(new Date(item.date), dateFormat)}
                    </span>
                  </div>
                  <div className="nk-activity-status ml-auto">
                    {item.isApproved ? (
                      <span className="badge badge-dim badge-success">
                        {t('Approved')}
                      </span>
                    ) : (
                      <span className="badge badge-dim badge-warning">
                        {t('Pending')}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default TradingOfferAcceptanceProgress;
